import { apiFetch } from "@/lib/api";

export type ClampQcInventoryBoxSnapshot = {
  hex: string;
  shortCode: string;
  sku: string;
  buildOrderId: number;
  workOrder: string;
  capacity: number;
  filledCount: number;
  sealed: boolean;
  ownerUserId: string | null;
  scanUrl: string;
};

export async function fetchClampQcOpenBoxForOperator(
  buildOrderId: number,
): Promise<{ box: ClampQcInventoryBoxSnapshot | null }> {
  return apiFetch<{ box: ClampQcInventoryBoxSnapshot | null }>(
    `/api/clamp-qc/box/open-for-operator?buildOrderId=${String(buildOrderId)}`,
  );
}

export async function fetchClampQcInventoryBox(hex: string): Promise<{ box: ClampQcInventoryBoxSnapshot }> {
  return apiFetch<{ box: ClampQcInventoryBoxSnapshot }>(`/api/clamp-qc/box/${encodeURIComponent(hex)}`);
}

export type ClampQcInventoryBoxPassedClampRow = {
  crate: string | null;
  timestampIso: string;
  boxSerial: number;
  serial: number;
  unloadedLine: number | null;
  loadedLineBeforeTester: number | null;
  loadedLineWithTester?: number | null;
  testerA: boolean;
  testerB: boolean;
  testerC: boolean;
  lineHarvestFail: boolean | null;
  inductance: number | null;
  inductancePassFail: string | null;
  failReason: string | null;
  observations: string | null;
  result: string;
  delta: number | null;
  clampSize: "large" | "small";
};

export async function fetchClampQcInventoryBoxPassedClamps(
  hex: string,
  buildOrderId: number,
): Promise<{ rows: ClampQcInventoryBoxPassedClampRow[] }> {
  return apiFetch<{ rows: ClampQcInventoryBoxPassedClampRow[] }>(
    `/api/clamp-qc/box/${encodeURIComponent(hex)}/passed-clamps?buildOrderId=${String(buildOrderId)}`,
  );
}

export async function openClampQcInventoryBoxAndPrint(input: {
  buildOrderId: number;
  capacity: number;
  printer: string;
}): Promise<{ box: ClampQcInventoryBoxSnapshot; printedQuantity: number }> {
  return apiFetch<{ box: ClampQcInventoryBoxSnapshot; printedQuantity: number }>(
    "/api/clamp-qc/box/open-and-print",
    {
      method: "POST",
      body: input,
    },
  );
}

export async function reprintClampQcInventoryBoxLabel(input: {
  hex: string;
  printer: string;
}): Promise<{ box: { hex: string; shortCode: string; quantity: number } }> {
  return apiFetch<{ box: { hex: string; shortCode: string; quantity: number } }>(
    `/api/clamp-qc/box/${encodeURIComponent(input.hex)}/reprint`,
    {
      method: "POST",
      body: { printer: input.printer },
    },
  );
}

export async function sealClampQcInventoryBox(input: {
  hex: string;
  buildOrderId: number;
  printer: string;
}): Promise<{ box: ClampQcInventoryBoxSnapshot; partialRelabel: boolean }> {
  return apiFetch<{ box: ClampQcInventoryBoxSnapshot; partialRelabel: boolean }>(
    `/api/clamp-qc/box/${encodeURIComponent(input.hex)}/seal`,
    {
      method: "POST",
      body: { buildOrderId: input.buildOrderId, printer: input.printer },
    },
  );
}

export async function removeClampFromInventoryBox(input: {
  hex: string;
  buildOrderId: number;
  serialText: string;
}): Promise<{ filledCount: number; box: ClampQcInventoryBoxSnapshot }> {
  return apiFetch<{ filledCount: number; box: ClampQcInventoryBoxSnapshot }>(
    `/api/clamp-qc/box/${encodeURIComponent(input.hex)}/remove-clamp`,
    {
      method: "POST",
      body: { buildOrderId: input.buildOrderId, serialText: input.serialText },
    },
  );
}
